import {
  COLLISION_PADDING,
  CAR_W,
  CAR_H,
  PATH_WIDTH,
  FLAG_WIDTH,
  FLAG_HEIGHT,
  FLAG_POLE_HEIGHT,
  FLAG_POLE_WIDTH,
  FLAG_OFFSET,
  DATE_TEXT_HEIGHT,
  DATE_TEXT_WIDTH,
  DATE_TEXT_OFFSET,
  BUILDINGS,
} from "../constants";
import type { Building, GameState, Tree } from "../types";
import type { PathSegment } from "./paths";

export const checkBuildingCollision = (
  carState: GameState,
  building: Building
): boolean => {
  const carRadius = Math.max(CAR_W, CAR_H) / 2;

  // Flag triangle bounding box
  // Flag extends from pole (at building.x) to the right
  // Pole is at building.x, building.y is bottom of pole
  const flagLeft = building.x + FLAG_POLE_WIDTH / 2; // Left edge at pole
  const flagRight = building.x + FLAG_POLE_WIDTH / 2 + FLAG_WIDTH; // Right edge of triangle
  const flagTop = building.y - FLAG_POLE_HEIGHT + FLAG_OFFSET; // Top of triangle
  const flagBottom = building.y - FLAG_POLE_HEIGHT + FLAG_OFFSET + FLAG_HEIGHT; // Bottom of triangle

  // Check collision with flag triangle area (not the pole)
  return (
    carState.x + carRadius > flagLeft - COLLISION_PADDING &&
    carState.x - carRadius < flagRight + COLLISION_PADDING &&
    carState.y + carRadius > flagTop - COLLISION_PADDING &&
    carState.y - carRadius < flagBottom + COLLISION_PADDING
  );
};

export const getBuildingDimensions = () => {
  return { w: FLAG_WIDTH, h: FLAG_HEIGHT };
};

// Check if a tree position overlaps with a building (flag)
export const isTreeOnBuilding = (tree: Tree, building: Building): boolean => {
  const treeRadius = 20; // Approximate tree radius

  // Flag triangle bounding box
  const flagLeft = building.x + FLAG_POLE_WIDTH / 2;
  const flagRight = building.x + FLAG_POLE_WIDTH / 2 + FLAG_WIDTH;
  const flagTop = building.y - FLAG_POLE_HEIGHT + FLAG_OFFSET;
  const flagBottom = building.y - FLAG_POLE_HEIGHT + FLAG_OFFSET + FLAG_HEIGHT;

  // Also check pole area to prevent trees from spawning on pole
  const poleLeft = building.x - FLAG_POLE_WIDTH / 2;
  const poleRight = building.x + FLAG_POLE_WIDTH / 2;
  const poleTop = building.y - FLAG_POLE_HEIGHT;
  const poleBottom = building.y;

  // Check collision with flag triangle or pole
  return (
    (tree.x + treeRadius > flagLeft &&
     tree.x - treeRadius < flagRight &&
     tree.y + treeRadius > flagTop &&
     tree.y - treeRadius < flagBottom) ||
    (tree.x + treeRadius > poleLeft &&
     tree.x - treeRadius < poleRight &&
     tree.y + treeRadius > poleTop &&
     tree.y - treeRadius < poleBottom)
  );
};

// Check if a tree position overlaps with any path segment
export const isTreeOnPath = (tree: Tree, paths: PathSegment[]): boolean => {
  const treeRadius = 20; // Approximate tree radius
  const pathRadius = PATH_WIDTH / 2 + treeRadius;

  for (const segment of paths) {
    // Calculate distance from tree to line segment
    const dx = segment.x2 - segment.x1;
    const dy = segment.y2 - segment.y1;
    const lengthSq = dx * dx + dy * dy;

    if (lengthSq === 0) {
      // Segment is a point
      const distSq = (tree.x - segment.x1) ** 2 + (tree.y - segment.y1) ** 2;
      if (distSq < pathRadius * pathRadius) return true;
    } else {
      // Calculate projection of tree onto line segment
      const t = Math.max(0, Math.min(1, 
        ((tree.x - segment.x1) * dx + (tree.y - segment.y1) * dy) / lengthSq
      ));
      
      const projX = segment.x1 + t * dx;
      const projY = segment.y1 + t * dy;
      
      const distSq = (tree.x - projX) ** 2 + (tree.y - projY) ** 2;
      if (distSq < pathRadius * pathRadius) return true;
    }
  }

  return false;
};

// Check if a position is valid (not on building or path)
export const isValidPosition = (
  x: number,
  y: number,
  radius: number = 15,
  paths: PathSegment[]
): boolean => {
  // Check flags (buildings are now flags) - includes flag, pole, and date text area
  const onBuilding = BUILDINGS.some((building) => {
    // Flag triangle bounding box
    const flagLeft = building.x + FLAG_POLE_WIDTH / 2;
    const flagRight = building.x + FLAG_POLE_WIDTH / 2 + FLAG_WIDTH;
    const flagTop = building.y - FLAG_POLE_HEIGHT + FLAG_OFFSET;
    const flagBottom = building.y - FLAG_POLE_HEIGHT + FLAG_OFFSET + FLAG_HEIGHT;

    // Pole area
    const poleLeft = building.x - FLAG_POLE_WIDTH / 2;
    const poleRight = building.x + FLAG_POLE_WIDTH / 2;
    const poleTop = building.y - FLAG_POLE_HEIGHT;
    const poleBottom = building.y;

    // Date text area (below flag)
    const dateTextTop = building.y - FLAG_POLE_HEIGHT + FLAG_OFFSET + FLAG_HEIGHT + DATE_TEXT_OFFSET;
    const dateTextLeft = building.x - DATE_TEXT_WIDTH / 2;
    const dateTextRight = building.x + DATE_TEXT_WIDTH / 2;
    const dateTextBottom = dateTextTop + DATE_TEXT_HEIGHT;

    // Check collision with flag triangle, pole, or date text area
    return (
      (x + radius > flagLeft &&
       x - radius < flagRight &&
       y + radius > flagTop &&
       y - radius < flagBottom) ||
      (x + radius > poleLeft &&
       x - radius < poleRight &&
       y + radius > poleTop &&
       y - radius < poleBottom) ||
      (x + radius > dateTextLeft &&
       x - radius < dateTextRight &&
       y + radius > dateTextTop &&
       y - radius < dateTextBottom)
    );
  });

  if (onBuilding) return false;

  // Check paths
  const onPath = isTreeOnPath({ x, y, type: 0 }, paths);
  if (onPath) return false;

  return true;
};

// Helper to check collision between car and a point (for trees/grass)
export const checkPointCollision = (
  px: number,
  py: number,
  radius: number,
  carX: number,
  carY: number,
  carW: number,
  carH: number
): boolean => {
  const carRadius = Math.max(carW, carH) / 2;
  const dx = px - carX;
  const dy = py - carY;
  const distance = Math.sqrt(dx * dx + dy * dy);
  return distance < carRadius + radius;
};

